'use strict';

import TextboxBase, { BorderStyle } from './textbox-base';
import FillInTheBlankParent, {
  FillInTheBlankAnswer,
} from './fill-in-the-blank-parent';
import FITBHandle from '../fitb-acceptable-answer-manager';
import { PrecheckOption } from './element';
import Point from './../point';
import Size from '../size';
import HexColors from '../../../css-constants';
import { UserRoles } from '../../domain/user';
import ElementMetadata, { ElementIntents } from '../../domain/element-metadata';
import checkIcon from '../../../../assets/colored-icons/check-dark.svg';
import closeIcon from '../../../../assets/colored-icons/close-dark.svg';
import { FitbAnswerTypes } from '../../../components/assignment-toolbar/assignment-toolbar.directive';
import { MathfieldElement } from 'mathlive';
import GradingUtils from '../../util/grading-utils.js';

export default class FillInTheBlankChild extends TextboxBase {
  /**
   * Create a new FillInTheBlankChild
   *
   * @param id {string}
   * @param metadata {ElementMetadata}
   * @param location {Point}
   * @param size {Size}
   * @param answers {FillInTheBlankAnswer[]}
   * @param [fontSize] {number}
   * @param [colorSpans] {array}
   * @param [answer] {string}
   * @param [format] {string}
   * @param [fulltext] {string}
   * @param [precheck] {string}
   * @param [question_metadata_id] {string}
   * @param [hint] {string}
   * @param [feedback] {string}
   */
  constructor(
    id,
    metadata,
    location,
    size,
    answers,
    fontSize,
    colorSpans,
    answer,
    format = FitbAnswerTypes.PLAIN.value,
    fulltext = null,
    precheck = PrecheckOption.NONE,
    questionMetadataId = null,
    hint = null,
    feedback = null,
  ) {
    super(id, FillInTheBlankChild.type, metadata, format);

    this._location = location || this._location;
    this._size = size;
    this._parentSize = size || this._size;
    this._fontSize = fontSize || this._fontSize;
    this._minSize = new Size(0, 0);
    this._padding = new Size(7, 7);
    this._colorSpans = colorSpans || this._colorSpans;
    this._text = fulltext ?? answer ?? this._text;
    this._rawAnswer = answer || this._text;
    this._answers = answers;
    this._submitted = !!this._text;
    this._placeholderText = '';
    this._borderColor = HexColors.CK_GREEN;
    this._editBoxBorderColor = 'transparent';
    this._format = format;
    this._displayMathKeyboard = false;

    this._enableResize = false;
    this._enableRemove = false;
    this._enableReposition = false;
    this._isTextBoxMinimized = false;
    this._minBoxHeight = TextboxBase.BOX_HEIGHT_MIN;
    this._fulltext = fulltext;
    this._precheck = precheck;
    this._questionMetadataId = questionMetadataId;
    this._hint = hint;
    this._feedback = feedback;

    this._readyForUpdate = false;

    this._isAttempted = false;
    this._showSpecialGrade = false;

    this.blurred.subscribe((ev) => this._handleBlurred(ev), this);
  }

  /**
   * @return {string}
   */
  static get type() {
    return 'fitb_child';
  }

  /**
   * @return {boolean}
   */
  get isChildManipulative() {
    return true;
  }

  get answerDisplay() {
    return this._text || '--';
  }

  resetFontSize() {
    if (!this.text) {
      let fontSize = this._fontSize;
      while (
        fontSize >
        this.height - this._padding.height - this._padding.height
      ) {
        fontSize -= TextboxBase.FONT_INCREMENT;
      }
      this._fontSize = fontSize;
    }
  }

  /**
   * @return {FillInTheBlankAnswer[]}
   */
  get answers() {
    return this._answers;
  }

  /**
   * @return {string}
   */
  get answer() {
    return this._text;
  }

  /**
   * @param value {string}
   */
  set answer(value) {
    this._rawAnswer = value;
    this._text = value;
  }

  get scoredPoints() {
    let answerstr = this._rawAnswer.trim().toLowerCase();
    return this.isCorrect
      ? this.answers.find((a) => a.answer.trim().toLowerCase() === answerstr)
          .points
      : 0;
  }

  get scoredPointsDisplay() {
    return angular.isNumber(this.scoredPoints) ? this.scoredPoints : '--';
  }

  get pointValue() {
    let answerstr = this._rawAnswer.trim().toLowerCase();
    return this.hasPotentiallyCorrectResponse ? this.potentialScore : undefined;
  }

  get pointOrPointsDisplay() {
    return this.pointValue === 1 ? 'Pt' : 'Pts';
  }

  get showScoreboardText() {
    return angular.isNumber(this.pointValue);
  }

  get showSpecialGradeText() {
    return angular.isString(this._rawAnswer) && this._rawAnswer !== '';
  }

  get format() {
    return this._format;
  }

  get precheck() {
    return this._precheck;
  }

  set precheck(value) {
    this._precheck = value;
  }

  get fulltext() {
    return this._fulltext;
  }

  set fulltext(value) {
    this._trackChange(() => {
      this._fulltext = value;
    });
  }

  get readyForUpdate() {
    return this._readyForUpdate;
  }

  set readyForUpdate(value) {
    this._readyForUpdate = value;
  }

  get hint() {
    return this._hint;
  }

  set hint(value) {
    this._hint = value;
  }

  get feedback() {
    return this._feedback;
  }

  set feedback(value) {
    this._feedback = value;
  }

  /**
   * Triggered when the text area loses focus.
   * Sets the submitted flag if the area is not empty, and attempts to update.
   * @private
   */
  _handleBlurred() {
    if (
      this.precheck === PrecheckOption.AI &&
      this.text !== this.fulltext &&
      this.text !== ''
    ) {
      this.readyForUpdate = true;
      this.editable = false;
      this.fulltext = this.text;

      // Start AI grading overlay when editable is false
      this._startAIGradingOverlay();
    }
    this._submitted = !!this.text;
    this.tryUpdate();
  }

  tabPressed() {
    this.focusNextElement.raise();
  }

  shiftTabPressed() {
    this.focusPrevElement.raise();
  }

  _startAIGradingOverlay() {
    if (this._aiGradingOverlay) {
      this._aiGradingOverlay.attr({ visibility: 'visible' });
    }
    this._startRainbowAnimation();
  }

  _stopAIGradingOverlay() {
    if (this._aiGradingOverlay) {
      this._aiGradingOverlay.attr({ visibility: 'hidden' });
    }
    this._stopRainbowAnimation();
    setTimeout(() => {
      this._hintTooltip?.attr({ visibility: 'visible' });
      setTimeout(() => {
        this._hintTooltip?.attr({ visibility: 'hidden' });
      }, 15000); // 15000 milliseconds = 15 seconds
    }, 100);
  }

  _startRainbowAnimation() {
    if (this._rainbowAnimationId) {
      this._stopRainbowAnimation();
    }

    const rainbowColors = [
      '#ff0000', // Red
      '#ff8000', // Orange
      '#ffff00', // Yellow
      '#80ff00', // Light Green
      '#00ff00', // Green
      '#00ff80', // Green-Cyan
      '#00ffff', // Cyan
      '#0080ff', // Light Blue
      '#0000ff', // Blue
      '#8000ff', // Purple
      '#ff00ff', // Magenta
      '#ff0080', // Pink
    ];

    const startTime = Date.now();
    const cycleDuration = 8000; // Complete rainbow cycle every 8 seconds

    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : null;
    };

    const rgbToHex = (r, g, b) => {
      return (
        '#' +
        (
          (1 << 24) +
          (Math.round(r) << 16) +
          (Math.round(g) << 8) +
          Math.round(b)
        )
          .toString(16)
          .slice(1)
      );
    };

    const interpolateColor = (color1, color2, factor) => {
      const rgb1 = hexToRgb(color1);
      const rgb2 = hexToRgb(color2);

      const r = rgb1.r + factor * (rgb2.r - rgb1.r);
      const g = rgb1.g + factor * (rgb2.g - rgb1.g);
      const b = rgb1.b + factor * (rgb2.b - rgb1.b);

      return rgbToHex(r, g, b);
    };

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = (elapsed % cycleDuration) / cycleDuration; // 0 to 1 progress through full cycle

      // Calculate which two colors we're interpolating between
      const colorSegment = progress * rainbowColors.length;
      const currentColorIndex = Math.floor(colorSegment) % rainbowColors.length;
      const nextColorIndex = (currentColorIndex + 1) % rainbowColors.length;
      const segmentProgress = colorSegment - Math.floor(colorSegment);

      // Smooth interpolation between colors
      this._currentRainbowColor = interpolateColor(
        rainbowColors[currentColorIndex],
        rainbowColors[nextColorIndex],
        segmentProgress,
      );

      this._updateRainbowBorder();

      this._rainbowAnimationId = requestAnimationFrame(animate);
    };

    this._rainbowAnimationId = requestAnimationFrame(animate);
  }

  _stopRainbowAnimation() {
    if (this._rainbowAnimationId) {
      cancelAnimationFrame(this._rainbowAnimationId);
      this._rainbowAnimationId = null;
      this._currentRainbowColor = null;
      this._updateRainbowBorder();
    }
  }

  _updateRainbowBorder() {
    if (!this._backgroundRect) return;

    if (
      this._currentRainbowColor &&
      this._aiGradingOverlay &&
      this._aiGradingOverlay.attr('visibility') === 'visible'
    ) {
      // Apply rainbow glow effect
      this._backgroundRect.attr({
        stroke: this._currentRainbowColor,
        strokeWidth: 10,
        filter: `drop-shadow(0 0 8px ${this._currentRainbowColor}) drop-shadow(0 0 16px ${this._currentRainbowColor}40)`,
      });

      if (this._editBox) {
        this._editBox.attr({
          stroke: this._currentRainbowColor,
          strokeWidth: 3,
          filter: `drop-shadow(0 0 8px ${this._currentRainbowColor}) drop-shadow(0 0 16px ${this._currentRainbowColor}40)`,
        });
      }
    } else {
      // Reset to normal border
      this._backgroundRect.attr({
        stroke: this.currentBorderColor,
        strokeWidth: 2,
        filter: 'none',
      });

      if (this._editBox) {
        this._editBox.attr({
          stroke: this.currentBorderColor,
          strokeWidth: 2,
          filter: 'none',
        });
      }
    }
  }

  _showHintIcon() {
    if (this.hint) {
      this._hintIcon.attr({ visibility: 'visible' });
    }
  }

  _setupHintTooltip(hintIconX, hintIconY, hintIconSize) {
    if (!this.hint) return;

    const maxTooltipWidth = 525;
    const tooltipPadding = 4;
    const lineHeight = 24;
    const fontSize = 22;

    // Create temp text to measure actual text dimensions
    const tempText = this._interactive.text(0, 0, this.hint);
    tempText.attr({
      fontFamily: 'GothamRoundedMedium',
      fontSize: `${fontSize}px`,
      visibility: 'hidden',
    });
    const textBBox = tempText.node.getBBox();
    const textWidth = textBBox.width;
    tempText.remove();

    // wrap text
    let wrappedLines = [];
    if (textWidth > maxTooltipWidth - tooltipPadding * 2) {
      const words = this.hint.split(' ');
      let currentLine = '';

      for (const word of words) {
        const testLine = currentLine ? `${currentLine} ${word}` : word;

        // Create temp text to measure this line
        const tempLineText = this._interactive.text(0, 0, testLine);
        tempLineText.attr({
          fontFamily: 'GothamRoundedMedium',
          fontSize: `${fontSize}px`,
          visibility: 'hidden',
        });
        const lineWidth = tempLineText.node.getBBox().width;
        tempLineText.remove();

        if (lineWidth > maxTooltipWidth - tooltipPadding * 2 && currentLine) {
          wrappedLines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }

      if (currentLine) {
        wrappedLines.push(currentLine);
      }
    } else {
      wrappedLines = [this.hint];
    }

    // Calculate final tooltip dimensions
    const tooltipWidth = Math.min(
      maxTooltipWidth,
      textWidth + tooltipPadding * 2,
    );
    const tooltipHeight = wrappedLines.length * lineHeight + tooltipPadding * 2;

    // Position tooltip to the right of the hint icon
    const tooltipX = hintIconX + hintIconSize + 8;
    const tooltipY = hintIconY + (hintIconSize - tooltipHeight) / 2;

    this._hintTooltipRect.attr({
      x: tooltipX,
      y: tooltipY,
      width: tooltipWidth,
      height: tooltipHeight,
      fill: HexColors.CK_AZURE_BLUE,
      rx: 4,
      ry: 4,
    });

    // Clear existing text and create multi-line text
    this._hintTooltipText.node.textContent = '';

    // Position the text element and add lines as tspan elements
    this._hintTooltipText.attr({
      x: tooltipX + tooltipPadding,
      y: tooltipY + tooltipPadding + fontSize,
      fontFamily: 'GothamRoundedMedium',
      fontSize: `${fontSize}px`,
      fill: '#FFFFFF',
      textAnchor: 'start',
      dominantBaseline: 'alphabetic',
    });

    // Add each line as a tspan element
    wrappedLines.forEach((line, index) => {
      const tspan = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'tspan',
      );
      tspan.textContent = line;
      tspan.setAttribute('x', tooltipX + tooltipPadding);
      tspan.setAttribute(
        'y',
        tooltipY + tooltipPadding + fontSize + index * lineHeight,
      );
      this._hintTooltipText.node.appendChild(tspan);
    });

    // Initially hide tooltip
    this._hintTooltip.attr({ visibility: 'hidden' });
  }

  /**
   * Fill in the blank children are not able to be repositioned.
   * When we encounter a drag, focus the element and pass the data
   * to the superclass.
   * @param data
   * @private
   */

  _dragStart(data) {
    this.focus();
    super._dragStart(data);
  }

  /**
   * @param text {string}
   * @param startIndex {string}
   * @param endIndex {string}
   */
  setText(text, startIndex, endIndex) {
    this._submitted = false;
    super.setText(text, startIndex, endIndex);
  }

  createElement(root, editable) {
    super.createElement(root, editable);

    this._scoreboard = this._interactive.group();
    this._scoreboardRect = this._scoreboard.rect(0, 0, 0, 0);
    this._scoreboardText = this._scoreboard.text(0, 0, 'test');
    this._scoreboardCheckIcon = this._scoreboard.image(checkIcon, 0, 0, 0, 0);
    this._scoreboardCloseIcon = this._scoreboard.image(closeIcon, 0, 0, 0, 0);
    this._specialGradeBoard = this._interactive.group();
    this._specialGradeRect = this._specialGradeBoard.rect(0, 0, 0, 0);
    this._specialGradeText = this._specialGradeBoard.text(0, 0, 'test');

    // Create AI grading overlay elements
    this._aiGradingOverlay = this._interactive.group();
    this._aiGradingOverlayRect = this._aiGradingOverlay.rect(0, 0, 0, 0);
    this._aiGradingOverlayText = this._aiGradingOverlay.text(
      0,
      0,
      '🤖 Instant Helper checking answer...',
    );

    // Create hint icon elements
    this._hintIcon = this._interactive.group();
    this._hintIconRect = this._hintIcon.rect(0, 0, 0, 0);
    this._hintIconText = this._hintIcon.text(0, 0, '💡');

    // Create hint tooltip elements
    this._hintTooltip = this._interactive.group();
    this._hintTooltipRect = this._hintTooltip.rect(0, 0, 0, 0);
    this._hintTooltipText = this._hintTooltip.text(0, 0, '');

    this._hintIcon.hover(
      () => {
        this._hintTooltip.attr({ visibility: 'visible' });
      },
      () => {
        this._hintTooltip.attr({ visibility: 'hidden' });
      },
    );

    this._hintIcon.touchstart(() => {
      const currentVisibility = this._hintTooltip.attr('visibility');
      if (currentVisibility === 'visible') {
        this._hintTooltip.attr({ visibility: 'hidden' });
      } else {
        this._hintTooltip.attr({ visibility: 'visible' });
      }
    });

    // Hide tooltip when touching elsewhere
    document.addEventListener('touchstart', (e) => {
      if (
        !this._hintIcon.node.contains(e.target) &&
        !this._hintTooltip.node.contains(e.target)
      ) {
        this._hintTooltip.attr({ visibility: 'hidden' });
      }
    });

    // Initially hide the elements
    this._aiGradingOverlay.attr({ visibility: 'hidden' });
    this._hintIcon.attr({ visibility: 'hidden' });
    this._hintTooltip.attr({ visibility: 'hidden' });

    // Create AI grading overlay elements
    this._aiGradingOverlay = this._interactive.group();
    this._aiGradingOverlayRect = this._aiGradingOverlay.rect(0, 0, 0, 0);
    this._aiGradingOverlayText = this._aiGradingOverlay.text(
      0,
      0,
      '🤖 Instant Helper checking answer...',
    );

    // Create hint icon elements
    this._hintIcon = this._interactive.group();
    this._hintIconRect = this._hintIcon.rect(0, 0, 0, 0);
    this._hintIconText = this._hintIcon.text(0, 0, '💡');

    // Create hint tooltip elements
    this._hintTooltip = this._interactive.group();
    this._hintTooltipRect = this._hintTooltip.rect(0, 0, 0, 0);
    this._hintTooltipText = this._hintTooltip.text(0, 0, '');

    this._hintIcon.hover(
      () => {
        this._hintTooltip.attr({ visibility: 'visible' });
      },
      () => {
        this._hintTooltip.attr({ visibility: 'hidden' });
      },
    );

    this._hintIcon.touchstart(() => {
      const currentVisibility = this._hintTooltip.attr('visibility');
      if (currentVisibility === 'visible') {
        this._hintTooltip.attr({ visibility: 'hidden' });
      } else {
        this._hintTooltip.attr({ visibility: 'visible' });
      }
    });

    // Hide tooltip when touching elsewhere
    document.addEventListener('touchstart', (e) => {
      if (
        !this._hintIcon.node.contains(e.target) &&
        !this._hintTooltip.node.contains(e.target)
      ) {
        this._hintTooltip.attr({ visibility: 'hidden' });
      }
    });

    // Initially hide the elements
    this._aiGradingOverlay.attr({ visibility: 'hidden' });
    this._hintIcon.attr({ visibility: 'hidden' });
    this._hintTooltip.attr({ visibility: 'hidden' });

    if (this.classCode && this.classCode.showStudentScoresUpdated) {
      this.classCode.showStudentScoresUpdated.subscribe(
        this._showStudentScoresUpdated,
        this,
      );
    }
  }

  update(root, editable) {
    this._borderColor = this.currentBorderColor;
    this._editBoxBorderColor = this.currentBorderColor;

    this._borderStyle = this.showSelected
      ? BorderStyle.SOLID
      : BorderStyle.DASHED;

    super.update(root, editable);

    let height = Math.max(this._parentSize.height, this.height);
    if (
      this._parentSize.height === this._minBoxHeight &&
      !this._isTextBoxMinimized
    ) {
      height = this._parentSize.height;
      this._fontSize = TextboxBase.FONT_SIZE_MIN;
      this._isTextBoxMinimized = true;
    } else if (this._parentSize.height !== this._minBoxHeight) {
      this._isTextBoxMinimized = false;
    }

    this._backgroundRect.attr({
      height,
    });

    this._editBox.attr({
      height,
    });

    if (editable) {
      this._interactRect?.attr({
        height,
        cursor: 'text',
      });
    }

    this._scoreboard.attr({
      visibility: this.showCorrect || this.showIncorrect ? 'inherit' : 'hidden',
    });

    const scoreboardPadding = 5;
    const scoreboardHeight = 30;
    const scoreboardContent =
      scoreboardHeight - scoreboardPadding - scoreboardPadding;

    this._scoreboardText.attr({
      text: this.getScoreBoardText(),
      dominantBaseline: 'text-before-edge',
      fontFamily: 'GothamRoundedMedium',
      fontSize: `${scoreboardContent}px`,
      fill: HexColors.CK_ELEMENT_BLACK,
    });

    const scoreboardTextWidth = this._scoreboardText.node.getBBox().width;
    const scoreboardWidth = scoreboardTextWidth
      ? scoreboardTextWidth + scoreboardPadding + scoreboardPadding
      : scoreboardHeight;
    const scoreboardX = this.location.x + this.width - scoreboardWidth + 1;
    const scoreboardY =
      this.location.y - scoreboardHeight + this._padding.height - 1;

    this._scoreboardRect.attr({
      x: scoreboardX,
      y: scoreboardY,
      rx: 2,
      ry: 2,
      width: scoreboardWidth,
      height: scoreboardHeight,
      fill: this.currentBorderColor,
    });

    this._scoreboardText.attr({
      x: scoreboardX + scoreboardPadding,
      y: scoreboardY + scoreboardPadding,
    });

    const iconAttributes = {
      x: scoreboardX + scoreboardPadding,
      y: scoreboardY + scoreboardPadding,
      width: scoreboardContent,
      height: scoreboardContent,
      pointerEvents: 'none',
      preserveAspectRatio: 'xMidYMid meet',
    };

    //TODO: setting this to hidden for now until we can fix the X
    // and check mark icon in PDF view, using emoji check and X for now
    this._scoreboardCheckIcon.attr({
      ...iconAttributes,
      visibility: 'hidden',
    });

    this._scoreboardCloseIcon.attr({
      ...iconAttributes,
      visibility: 'hidden',
    });

    // Update AI grading overlay positioning and styling
    if (this._aiGradingOverlay) {
      const overlayX = this.location.x;
      const overlayY = this.location.y;
      const overlayWidth = this.width;
      const overlayHeight = height;

      this._aiGradingOverlayRect.attr({
        x: overlayX,
        y: overlayY,
        width: overlayWidth,
        height: overlayHeight,
        fill: 'rgba(240, 242, 247, 0.95)', // Light greyish-blue overlay for disabled state
        stroke: 'rgba(200, 210, 220, 0.8)',
        strokeWidth: 1,
        rx: 2,
        ry: 2,
      });

      this._aiGradingOverlayText.attr({
        x: overlayX + overlayWidth / 2,
        y: overlayY + overlayHeight / 2,
        textAnchor: 'middle',
        dominantBaseline: 'central',
        fontFamily: 'GothamRoundedMedium',
        fontSize: '25px',
        fontWeight: '500',
        fill: '#000000',
        text: '🤖 Instant Helper checking answer...',
      });
    }

    // Update AI grading overlay positioning and styling
    if (this._aiGradingOverlay) {
      const overlayX = this.location.x;
      const overlayY = this.location.y;
      const overlayWidth = this.width;
      const overlayHeight = height;

      this._aiGradingOverlayRect.attr({
        x: overlayX,
        y: overlayY,
        width: overlayWidth,
        height: overlayHeight,
        fill: 'rgba(240, 242, 247, 0.95)', // Light greyish-blue overlay for disabled state
        stroke: 'rgba(200, 210, 220, 0.8)',
        strokeWidth: 1,
        rx: 2,
        ry: 2,
      });

      this._aiGradingOverlayText.attr({
        x: overlayX + overlayWidth / 2,
        y: overlayY + overlayHeight / 2,
        textAnchor: 'middle',
        dominantBaseline: 'central',
        fontFamily: 'GothamRoundedMedium',
        fontSize: '25px',
        fontWeight: '500',
        fill: '#000000',
        text: '🤖 Instant Helper checking answer...',
      });
    }

    if (this.precheck === PrecheckOption.AI) {
      this._specialGradeBoard.attr({
        visibility: this.isSpecialGradeVisible(),
      });

      const specialGradePadding = 5;
      const specialGradeHeight = 30;
      const specialGradeContent =
        specialGradeHeight - specialGradePadding - specialGradePadding;

      this._specialGradeText.attr({
        text: this.getSpecialGradeText(),
        dominantBaseline: 'text-before-edge',
        fontFamily: 'GothamRoundedMedium',
        fontSize: `${specialGradeContent}px`,
        fill: HexColors.CK_ELEMENT_BLACK,
      });

      const specialGradeTextWidth = this._specialGradeText.node.getBBox().width;
      const specialGradeWidth = specialGradeTextWidth
        ? specialGradeTextWidth + specialGradePadding + specialGradePadding
        : specialGradeHeight;
      const specialGradeX = this.location.x;
      const specialGradeY =
        this.location.y - specialGradeHeight + this._padding.height - 1;

      this._specialGradeRect.attr({
        x: specialGradeX,
        y: specialGradeY,
        rx: 2,
        ry: 2,
        width: specialGradeWidth,
        height: specialGradeHeight,
        fill: this.currentBorderColor,
      });

      this._specialGradeText.attr({
        x: specialGradeX + specialGradePadding,
        y: specialGradeY + specialGradePadding,
      });

      // Update hint icon positioning and styling
      if (this._hintIcon) {
        const hintIconSize = 35;
        const hintIconPadding = 6;
        const hintIconX =
          this.location.x +
          specialGradeWidth +
          hintIconPadding +
          hintIconPadding;
        const hintIconY = this.location.y - hintIconSize + 8;

        this._hintIconRect.attr({
          x: hintIconX,
          y: hintIconY,
          width: hintIconSize,
          height: hintIconSize,
          fill: HexColors.CK_AZURE_BLUE,
          rx: 4,
          ry: 4,
        });

        this._hintIconText.attr({
          x: hintIconX + hintIconSize / 2,
          y: hintIconY + hintIconSize / 2,
          textAnchor: 'middle',
          dominantBaseline: 'central',
          fontSize: '16px',
          text: '💡',
          pointerEvents: 'none',
        });

        // Show hint icon only if there's a hint and the element is not in AI grading mode
        if (
          this.hint &&
          this._aiGradingOverlay.attr('visibility') !== 'visible'
        ) {
          this._hintIcon.attr({ visibility: 'visible' });
          this._setupHintTooltip(hintIconX, hintIconY, hintIconSize);
        } else {
          this._hintIcon.attr({ visibility: 'hidden' });
          this._hintTooltip.attr({ visibility: 'hidden' });
        }
      }
    }

    //when user switches from STEM to plain, it should remove the math-field element tied to this FITB
    if (this._format === FitbAnswerTypes.PLAIN.value) {
      const mathFields = document.querySelectorAll(
        `foreignObject.fo-${this.id}`,
      );
      if (mathFields.length) {
        Array.from(mathFields).forEach((element) => {
          element.remove();
        });
      }
    }

    if (this._format === FitbAnswerTypes.SCIENTIFIC.value) {
      const parentElements = Array.from(
        document.getElementsByClassName(this.id),
      );
      if (parentElements.length) {
        this._createOrUpdateForeignObjectAndMathFieldElements(
          parentElements,
          editable,
        );
      }
    }

    // provide notification to the teacher when child response does not match list of acceptable answers
    if (this.viewerIsTeacher && this._text !== '' && this._answers.length > 0) {
      this._fitbHandle = new FITBHandle(this);
      this._fitbHandle.render(this._interactive);
      this._fitbHandle.location = this.location.plus(
        new Point(-27, Math.max(this._parentSize.height, this.height) - 24),
      );
      this._fitbHandle.visibility = this.isCorrect ? 'hidden' : 'visible';
    }

    // handle case where student resets a given answer; then remove the notification
    if (this._text === '' && this._fitbHandle && this._answers.length > 0) {
      this._fitbHandle.visibility = 'hidden';
    }

    // Update rainbow border animation
    this._updateRainbowBorder();
  }

  getScoreBoardText() {
    if (this.showScoreboardText) {
      return `${this.scoredPointsDisplay}/${this.pointValue} ${this.pointOrPointsDisplay}`;
    } else if (this.showCorrect) {
      return '✔️';
    } else if (this.showIncorrect) {
      return '✖️';
    } else {
      return '';
    }
  }

  isSpecialGradeVisible() {
    if (this._isAttempted || this.isCorrect) {
      if (this._showSpecialGrade || this.isCorrect) {
        return 'visible';
      } else {
        return 'hidden';
      }
    } else {
      return 'hidden';
    }
  }

  getSpecialGradeText() {
    if (this.showSpecialGradeText) {
      let answerstr = this._rawAnswer.trim().toLowerCase();
      return this.answers.find(
        (a) => a.answer.trim().toLowerCase() === answerstr,
      )?.answer;
    } else {
      return '';
    }
  }

  // prepare data in format to update list of acceptable answers
  trackIncorrectResponseToBeMarkedCorrect() {
    const studentResponse = this._text.trim().toLowerCase();
    const assignmentId = this._assignment.assignment.id;

    const newFillInTheBlankAnswer = new FillInTheBlankAnswer(
      studentResponse,
      this._answers[0].points,
    );

    // add the new response into accepted answers
    const newAnswerArray = [];
    newAnswerArray.push(this._answers);
    newAnswerArray.push(newFillInTheBlankAnswer);

    let elementMetaData = new ElementMetadata(
      this.viewerMetadata['userId'],
      'teacher',
      'work',
    );

    const newFillInTheBlankParent = new FillInTheBlankParent(
      this.id,
      elementMetaData,
      this._location,
      this._size,
      newAnswerArray.flat(),
      this._fontSize,
      this._format,
    );
    // newFillInTheBlankParent._answers = newAnswerArray.flat();

    return {
      childElementId: this.id,
      assignmentId: assignmentId,
      newFillInTheBlankParent: newFillInTheBlankParent,
      studentResponse: studentResponse,
    };
  }

  _showStudentScoresUpdated() {
    this.tryUpdate();
  }

  get normalBorderColor() {
    if (this.showCorrect) {
      return HexColors.CK_GRADING_MEETS;
    } else if (this.showIncorrect) {
      return HexColors.CK_GRADING_BELOW;
    } else if (this.showSelected) {
      return HexColors.CK_ELEMENT_GREY;
    } else {
      return HexColors.CK_GREEN;
    }
  }

  get aiBorderColor() {
    if (this.showCorrect) {
      const percentScore = this.scoredPointsDisplay / this.pointValue;
      return GradingUtils.colorForScore(percentScore);
    } else if (this.showSelected) {
      return HexColors.CK_ELEMENT_GREY;
    } else {
      return HexColors.CK_ELEMENT_GREEN;
    }
  }

  get currentBorderColor() {
    if (this.precheck === PrecheckOption.AI) {
      return this.aiBorderColor;
    } else {
      return this.normalBorderColor;
    }
  }

  get showCorrect() {
    return this.canShowCorrectness && this.isCorrect;
  }

  get showIncorrect() {
    return this.canShowCorrectness && !this.isCorrect && !!this.text;
  }

  get canShowCorrectness() {
    if (this.viewerIsTeacher || this.viewerIsNotMainTeacher) {
      return this.hasPotentiallyCorrectResponse;
    } else if (this.viewerIsStudentDoingWork) {
      return (
        (this._submitted || !!this._text) &&
        this.showStudentScores &&
        this.hasPotentiallyCorrectResponse
      );
    } else {
      return false;
    }
  }

  get hasPotentiallyCorrectResponse() {
    return this._answers.length > 0;
  }

  get showSelected() {
    return this.hasFocus || !!this.text;
  }

  get viewerIsStudentDoingWork() {
    return this.viewerIsDoingWork && this.viewerIsStudent;
  }

  get viewerIsDoingWork() {
    return this.viewerIntent && this.viewerIntent === ElementIntents.WORK;
  }

  /**
   * @return {boolean}
   */
  get viewerIsTeacher() {
    return (
      this.viewerRole &&
      this.viewerRole === UserRoles.TEACHER &&
      this.viewerIntent === ElementIntents.FEEDBACK
    );
  }

  /**
   * @return {boolean}
   */
  get viewerIsNotMainTeacher() {
    return (
      this.viewerRole &&
      this.viewerRole === UserRoles.TEACHER &&
      this.viewerIntent === ElementIntents.VIEW
    );
  }

  /**
   * @return {boolean}
   */
  get viewerIsStudent() {
    return this.viewerRole && this.viewerRole === UserRoles.STUDENT;
  }

  /**
   * @return {boolean}
   */
  get showStudentScores() {
    return this.classCode && this.classCode.showStudentScores;
  }

  /**
   * @return {ClassCode}
   */
  get classCode() {
    return this.assignment && this.assignment.classCode;
  }

  get isCorrect() {
    return this._answers.some((answer) => {
      let acceptableAnswer = answer.answer.trim().toLowerCase();
      let studentResponse = this._rawAnswer.trim().toLowerCase();
      return studentResponse === acceptableAnswer;
    });
  }

  get potentialScore() {
    return this._answers
      .map((answer) => answer.points)
      .reduce((accum, answerPoints) => {
        return accum > answerPoints ? accum : answerPoints;
      });
  }

  get questionMetadataId() {
    return this._questionMetadataId;
  }

  set questionMetadataId(value) {
    this._questionMetadataId = value;
  }

  remove() {
    if (this.classCode) {
      this.classCode.showStudentScoresUpdated.unsubscribe(
        this._showStudentScoresUpdated,
        this,
      );
    }
    super.remove();
  }

  /**
   * Merges properties from another instance of the same class into this object
   * @param other {FillInTheBlankChild}
   * @param forceUpdate {boolean}
   */
  merge(other, forceUpdate) {
    this._metadata = other._metadata || this._metadata;
    this._location = other._location || this._location;
    this._size = other._size || this._size;
    this._parentSize = other._size || this._parentSize;
    this._answers = other._answers || this._answers;
    this._fontSize = other._fontSize || this._fontSize;
    this._colorSpans = other._colorSpans || this._colorSpans;
    this._format = other._format || this._format;
    // Use other text when undoing/redoing (aka forceUpdate) or when the viewer of this fitb is a teacher or peer giving feedback
    this._text =
      angular.isDefined(other._text) &&
      (forceUpdate || !this.viewerIsStudentDoingWork)
        ? other._text
        : this._text;
    this._rawAnswer = other._rawAnswer;

    // Stop AI grading overlay when editable is true
    this._stopAIGradingOverlay();

    if (!this._isAttempted) {
      this._isAttempted = true;
    }
    this._fulltext = other.fulltext || this.fulltext;
    this._precheck = other.precheck || this.precheck;
    this._questionMetadataId =
      other.questionMetadataId || this.questionMetadataId;
    this._hint = other.hint || this.hint;
    this._feedback = other.feedback || this.feedback;

    this._showHintIcon();

    this._showSpecialGrade = true;
    this.editable = true;
    if (!this._isAttempted) {
      this._isAttempted = true;
    }
    this.tryUpdate();
  }

  /**
   * Extracts the persisted values from this entity into something compatible with the merge function
   * @returns {object}
   */
  snapshot() {
    return {
      _metadata: this._metadata,
      _location: this._location,
      _size: this._size,
      _answers: this._answers,
      _fontSize: this._fontSize,
      _colorSpans: this._colorSpans,
      _answer: this._text,
      _text: this._text,
    };
  }

  /**
   * Creates a new element from a snapshot
   * @param id {string}
   * @param snapshot {object}
   * @returns {FillInTheBlankChild}
   */
  fromSnapshot(id, snapshot) {
    return new FillInTheBlankChild(
      id,
      snapshot._metadata,
      snapshot._location,
      snapshot._size,
      snapshot._answers,
      snapshot._fontSize,
      snapshot._colorSpans,
      snapshot._answer,
      snapshot._format,
    );
  }

  /**
   * @param element {Element}
   * @param editable {boolean}
   */
  _createForeignObjectAndMathFieldElement(element, editable) {
    const ns = 'http://www.w3.org/2000/svg';
    const foreignObject = document.createElementNS(ns, 'foreignObject');
    foreignObject.classList.add(`fo-${this.id}`);

    //set math-field value to text value
    const mfe = new MathfieldElement();
    mfe.value = this._text;

    //add math-field to foreign object and then foreign object to body
    foreignObject.append(mfe);
    element.append(foreignObject);

    //set attributes and styling to math-field element
    this._applyStylingAndAttributesToForeignObjectAndMathFieldElements(
      element,
      editable,
    );

    //listens for changes to math-field height and then updates foreignObject height to match
    const foElements = Array.from(
      document.getElementsByClassName(`fo-${this.id}`),
    );
    foElements.forEach((foElement) => {
      const mfeDomElement = foElement.getElementsByClassName(
        `mf-${this.id}`,
      )[0];
      if (mfeDomElement) {
        const resizeObserver = new ResizeObserver(() => {
          foElement.setAttribute(
            'height',
            mfeDomElement.offsetHeight.toString(),
          );
          if (this._fitbHandle) {
            this._fitbHandle.location = this.location.plus(
              new Point(-27, mfeDomElement.offsetHeight - 24),
            );
          }
        });
        resizeObserver.observe(mfeDomElement);
      }
    });

    const keyboard = mfe.shadowRoot.querySelector(
      '.ML__virtual-keyboard-toggle',
    );
    const textElem = document.createElement('span');
    keyboard.parentNode.insertBefore(textElem, keyboard);
    textElem.innerHTML = 'Toggle Virtual Keyboard';
    textElem.classList.add('tooltiptext');

    const tooltipStyling = `
      background-color:#616161;
      color:#fff;
      padding:8px;
      font-size:11px;
      width:135px;
      visibility:hidden;
      position:absolute;
      right: 44px;
      top: 50%;
      transform: translate(0%, -50%);
      box-shadow: rgba(0, 0, 0, 0.14) 0px 2px 2px 0px, rgba(0, 0, 0, 0.12) 0px 1px 5px 0px, rgba(0, 0, 0, 0.2) 0px 3px 1px -2px;
      text-align: center;
      transition: all 0.15s cubic-bezier(0.4, 0, 1, 1) 0s;`;

    textElem.style = tooltipStyling;

    this._addMathFieldEventListeners(mfe, editable);
  }

  /**
   * @param parentElements {Element[]}
   * @param editable {boolean}
   */
  _createOrUpdateForeignObjectAndMathFieldElements(parentElements, editable) {
    parentElements.forEach((parentElement) => {
      const interactiveElements =
        parentElement.getElementsByClassName('interactive');

      Array.from(interactiveElements).forEach((element) => {
        const mathItem = element.getElementsByTagName('math-field');

        if (
          element &&
          this._format === FitbAnswerTypes.SCIENTIFIC.value &&
          !mathItem.length
        ) {
          this._createForeignObjectAndMathFieldElement(element, editable);
        } else {
          this._applyStylingAndAttributesToForeignObjectAndMathFieldElements(
            element,
            editable,
          );
        }
      });
    });
  }

  /**
   * @param element {Element}
   * @param editable {boolean}
   */
  _applyStylingAndAttributesToForeignObjectAndMathFieldElements(
    element,
    editable,
  ) {
    const foreignObject = element.querySelectorAll(`.fo-${this.id}`)[0];
    const mathFieldElement = element.getElementsByTagName('math-field')[0];

    if (foreignObject) {
      foreignObject.setAttribute('x', this.location.x + 1);
      foreignObject.setAttribute('y', this.location.y + 4);
      foreignObject.setAttribute('width', `${this.width}`);
      foreignObject.setAttribute(
        'height',
        `${element.getElementsByTagName('math-field')[0].offsetHeight + 1}`,
      );
    }

    const borderStyle = this._text ? BorderStyle.SOLID : BorderStyle.DASHED;
    mathFieldElement.value = this._text;
    mathFieldElement.style.border = `2px ${borderStyle} ${this.currentBorderColor}`;
    mathFieldElement.style.borderStyle = BorderStyle.DEFAULT_DASH_SPACING;
    mathFieldElement.classList.add(`mf-${this.id}`);
    mathFieldElement.style.fontSize = '2.5rem';
    mathFieldElement.style.height = 'auto';
    mathFieldElement.style.width = `${this.width - 12}px`;
    mathFieldElement.readonly = editable ? false : true;
  }

  /**
   * @param text {string}
   */
  _setMathText(text) {
    this._text = text;
  }

  /**
   * @param event {Event}
   */
  _handleMathFieldInput(event) {
    //this is to allows the autosuggest in math-field to work
    if (event.data === 'insertText' && event.inputType === 'insertText') {
      return event.preventDefault();
    }
    if (event.data === 'insertLineBreak') {
      window.mathVirtualKeyboard.hide();
    }
    this._setMathText(event.target.value);
  }

  _openMathKeyboard(mathFieldElement) {
    this._displayMathKeyboard = true;
    mathFieldElement.focus();
    window.mathVirtualKeyboard.show();
  }

  _closeMathKeyboard(trackChange) {
    this._displayMathKeyboard = false;
    window.mathVirtualKeyboard.hide();
    trackChange(() => {});
  }

  _toggleMathKeyboard(mathFieldElement) {
    return this._displayMathKeyboard
      ? this._closeMathKeyboard(this._trackChange.bind(this))
      : this._openMathKeyboard(mathFieldElement);
  }

  /**
   * @param mathFieldElement {HTMLElement}
   */
  _handleMathFieldFocus(mathFieldElement) {
    mathFieldElement.style.outline = this.currentBorderColor;
    mathFieldElement.style.border = `2px solid ${this.currentBorderColor}`;
    this._openMathKeyboard(mathFieldElement);
  }

  /**
   * @param mathFieldElement {HTMLElement}
   * @param borderStyle {string}
   */
  _handleMathFieldMouseOut(mathFieldElement, borderStyle) {
    if (!this._text) {
      mathFieldElement.style.border = `2px ${borderStyle} ${this.currentBorderColor}`;
    }
  }

  _keyboardMouseOver(element) {
    element.parentNode.querySelector('.tooltiptext').style.visibility =
      'visible';
  }

  _keyboardMouseOut(element) {
    element.parentNode.querySelector('.tooltiptext').style.visibility =
      'hidden';
  }

  /**
   * @param mathFieldElement {HTMLElement}
   */
  _handleMathFieldMouseOver(mathFieldElement) {
    mathFieldElement.style.border = `2px solid ${this.currentBorderColor}`;
  }

  /**
   * @param mathFieldElement {HTMLElement}
   */
  _addMathFieldEventListeners(mathFieldElement, editable) {
    const borderStyle = this._text ? BorderStyle.SOLID : BorderStyle.DASHED;
    this.toggleKeyboard = this._toggleMathKeyboard.bind(this);

    if (editable) {
      //if event listener does not exist, add and set attribute
      if (mathFieldElement.getAttribute('inputListener') !== 'true') {
        mathFieldElement.addEventListener('input', (event) =>
          this._handleMathFieldInput(event),
        );
        mathFieldElement.setAttribute('inputListener', 'true');
      }

      if (mathFieldElement.getAttribute('focusListener') !== 'true') {
        mathFieldElement.addEventListener('focus', (event) =>
          this._handleMathFieldFocus(mathFieldElement),
        );
        mathFieldElement.setAttribute('focusListener', 'true');
      }

      if (mathFieldElement.getAttribute('blurListener') !== 'true') {
        mathFieldElement.addEventListener('blur', () =>
          this._closeMathKeyboard(this._trackChange.bind(this)),
        );
        mathFieldElement.setAttribute('blurListener', 'true');
      }

      const keyboard = mathFieldElement.shadowRoot.querySelector(
        '.ML__virtual-keyboard-toggle',
      );
      if (keyboard && keyboard.getAttribute('clickListener') !== 'true') {
        keyboard.addEventListener('click', () =>
          this.toggleKeyboard(mathFieldElement),
        );
        mathFieldElement.setAttribute('clickListener', 'true');
      }

      if (keyboard && keyboard.getAttribute('mouseoverListener') !== 'true') {
        keyboard.addEventListener('mouseover', () =>
          this._keyboardMouseOver(keyboard),
        );
        keyboard.setAttribute('mouseoverListener', 'true');
      }

      if (keyboard && keyboard.getAttribute('mouseoutListener') !== 'true') {
        keyboard.addEventListener('mouseout', () =>
          this._keyboardMouseOut(keyboard),
        );
        keyboard.setAttribute('mouseoutListener', 'true');
      }
    }

    if (mathFieldElement.getAttribute('mouseoutListener') !== 'true') {
      mathFieldElement.addEventListener('mouseout', () =>
        this._handleMathFieldMouseOut(mathFieldElement, borderStyle),
      );
      mathFieldElement.setAttribute('mouseoutListener', 'true');
    }

    if (mathFieldElement.getAttribute('mouseoverListener') !== 'true') {
      mathFieldElement.addEventListener('mouseover', () =>
        this._handleMathFieldMouseOver(mathFieldElement),
      );
      mathFieldElement.setAttribute('mouseoverListener', 'true');
    }
  }

  removeMathFieldEventListeners() {
    const borderStyle = this._text ? BorderStyle.SOLID : BorderStyle.DASHED;
    const mathFieldElements = Array.from(
      document.getElementsByClassName(`mf-${this.id}`),
    );

    mathFieldElements.forEach((mathFieldElement) => {
      if (mathFieldElement.getAttribute('inputListener') === 'true') {
        mathFieldElement.removeEventListener('input', (event) =>
          this._handleMathFieldInput(event),
        );
        mathFieldElement.setAttribute('inputListener', 'false');
      }

      if (mathFieldElement.getAttribute('focusListener') === 'true') {
        mathFieldElement.removeEventListener('focus', (event) =>
          this._handleMathFieldFocus(mathFieldElement),
        );
        mathFieldElement.setAttribute('focusListener', 'false');
      }

      if (mathFieldElement.getAttribute('blurListener') === 'true') {
        mathFieldElement.removeEventListener('blur', () =>
          this._closeMathKeyboard(this._trackChange.bind(this)),
        );
        mathFieldElement.setAttribute('blurListener', 'false');
      }

      if (mathFieldElement.getAttribute('mouseoutListener') === 'true') {
        mathFieldElement.removeEventListener('mouseout', () =>
          this._handleMathFieldMouseOut(mathFieldElement, borderStyle),
        );
        mathFieldElement.setAttribute('mouseoutListener', 'false');
      }

      if (mathFieldElement.getAttribute('mouseoverListener') === 'true') {
        mathFieldElement.removeEventListener('mouseover', () =>
          this._handleMathFieldMouseOver(mathFieldElement),
        );
        mathFieldElement.setAttribute('mouseoverListener', 'false');
      }

      const keyboard = mathFieldElement.shadowRoot.querySelector(
        '.ML__virtual-keyboard-toggle',
      );
      if (keyboard && keyboard.getAttribute('clickListener') === 'true') {
        keyboard.removeEventListener('click', () =>
          this.toggleKeyboard(mathFieldElement),
        );
        mathFieldElement.setAttribute('clickListener', 'false');
      }

      if (keyboard && keyboard.getAttribute('mouseoverListener') === 'true') {
        keyboard.removeEventListener('mouseover', () =>
          this._keyboardMouseOver(keyboard),
        );
        keyboard.setAttribute('mouseoverListener', 'false');
      }

      if (keyboard && keyboard.getAttribute('mouseoutListener') === 'true') {
        keyboard.removeEventListener('mouseout', () =>
          this._keyboardMouseOut(keyboard),
        );
        keyboard.setAttribute('mouseoutListener', 'false');
      }
    });
  }
}
